import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  ExportOrderBookExcelRequest,
  ExportOrderBookToPdfRequest,
  ExportPdfType,
  FindOrderMaterialsResponseDto,
  FindOrdersRequestDto,
  FindOrdersResponse,
  GroupedOrdersResponse,
  InvoicesSearchResult,
  IOrdersGroupType,
  OrderDetail,
  OrderFiltersResponse,
} from '@epicuro-next/models/order-book';

import { ORDERS_FILTERS_V2_API_TOKEN, ORDERS_V2_API_TOKEN } from '../../tokens';

@Injectable()
export class OrdersV2Service {
  private readonly httpClient = inject(HttpClient);
  private readonly ordersV2Url = inject(ORDERS_V2_API_TOKEN);
  private readonly ordersV2FiltersUrl = inject(ORDERS_FILTERS_V2_API_TOKEN, {
    optional: true,
  });

  public getFilters(query: FindOrdersRequestDto) {
    return this.httpClient.post<OrderFiltersResponse>(
      `${this.ordersV2Url}/${this.ordersV2FiltersUrl}`,
      query,
    );
  }

  public getOrders(query: FindOrdersRequestDto) {
    return this.httpClient.post<FindOrdersResponse>(
      `${this.ordersV2Url}`,
      query,
    );
  }

  public getMaterials(query: FindOrdersRequestDto) {
    return this.httpClient.post<FindOrderMaterialsResponseDto>(
      `${this.ordersV2Url}/materials`,
      query,
    );
  }

  public getMaterialsGrouped(
    query: FindOrdersRequestDto,
    groupBy: IOrdersGroupType,
  ) {
    return this.httpClient.post<GroupedOrdersResponse>(
      `${this.ordersV2Url}/materials/grouped/${groupBy}`,
      query,
    );
  }

  public getMaterialsInvoices(query: FindOrdersRequestDto) {
    return this.httpClient.post<InvoicesSearchResult>(
      `${this.ordersV2Url}/materials/invoices`,
      query,
    );
  }

  public downloadOrder(orderNumber: string) {
    return this.httpClient.post(
      `${this.ordersV2Url}/export/download/${orderNumber}`,
      {},
      {
        responseType: 'text',
        observe: 'response',
      },
    );
  }

  public exportToXls(
    exportRequest: ExportOrderBookExcelRequest,
    executeSynchronous = true,
  ) {
    return this.httpClient.post(
      `${this.ordersV2Url}/export/excel?executeSynchronous=${executeSynchronous}`,
      exportRequest,
      {
        responseType: 'text',
        observe: 'response',
      },
    );
  }

  public exportInvoicesToXls(
    exportRequest: ExportOrderBookExcelRequest,
    executeSynchronous = true,
  ) {
    return this.httpClient.post(
      `${this.ordersV2Url}/export/invoices/excel?executeSynchronous=${executeSynchronous}`,
      exportRequest,
      {
        responseType: 'text',
        observe: 'response',
      },
    );
  }

  public exportToPdf(
    exportRequest: ExportOrderBookToPdfRequest,
    type: ExportPdfType,
  ) {
    return this.httpClient.post<string>(
      `${this.ordersV2Url}/export/pdf/${type}`,
      exportRequest,
    );
  }

  public downloadFileByUrl(url: string) {
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  public getAddressNames(addressIds: string[]) {
    return this.httpClient.post<Record<string, string>>(
      `${this.ordersV2Url}/address/names`,
      addressIds,
    );
  }

  public getBrandNames(brandIds: string[]) {
    return this.httpClient.post<Record<string, string>>(
      `${this.ordersV2Url}/materials/brand/names`,
      brandIds,
    );
  }

  public getAccountNames(accountIds: string[]) {
    return this.httpClient.post<Record<string, string>>(
      `${this.ordersV2Url}/materials/account/names`,
      accountIds,
    );
  }

  public getOrderDetails(orderNumber: string) {
    return this.httpClient.get<OrderDetail>(
      `${this.ordersV2Url}/${orderNumber}`,
    );
  }
}
